import { Segment, Icon, Grid, Popup } from 'semantic-ui-react';

import { ButtonLink, ChatThread, ClippedContent, CloseableContent, EmptyListPlaceholder, TextingControls } from 'dyl-components';
import MessageEditor from 'shared/MessageEditor';
import { useSelector } from 'react-redux';
import { PhoneUtil, StringUtils } from 'utils';
import { Person } from 'dyl-components';
import { Link } from 'react-router-dom';
import PHONE_ICON from 'shared/constants/PHONE_ICON';

import './index.scss';
import useModal from 'utils/useModal';
import AddContactModal from 'shared/modals/AddContactModal';
import { PRODUCT_PERMISSIONS, productRestriction } from 'dyl-components';

function ChatBox({ 
    messages, 
    isReadingMessages, 
    recipientId, 
    recipientName, 
    isLoadingRecipientDetails, 
    recipientOptions, 
    userOptions, 
    onReadMessages, 
    onReplaceRecipientVariable, 
    isAccount,
    isOfficeView,
    from,
    to,
    onChangeNumbers,
    selectedTextActivityLog
}) {
    const productPermissions = useSelector(state => state.subscription.productPermissions);
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);
    
    const textPhones = useSelector(state => state.text.phones?.map(phone => ({
        key: phone,
        value: phone,
        text: PhoneUtil.formatPhoneNumber(phone)
    })) || []);
    
    const isUnknownContact = recipientName === "Unknown";
    const addContactModal = useModal();
    const recipientPhones = isUnknownContact ? [
        {
            key: to,
            value: to,
            text: PhoneUtil.formatPhoneNumber(to),
            selectedOptionFormat: (
                <Person
                    username={recipientName}
                    subtitleLabel="Phone"
                    email={PhoneUtil.formatPhoneNumber(to)} />
            )
        }
    ] : recipientOptions.map(({ id, phone, main, phone_type }) => {
        const formattedPhone = PhoneUtil.formatPhoneNumber(phone);
        return ({
            key: id,
            value: phone,
            text: formattedPhone,
            content: (

                <div className='ChatBox__dropdownOption--to'>
                    <Icon name={PHONE_ICON[StringUtils.capitalize(phone_type)] || 'phone'} /> {formattedPhone}  {main && <Icon
                        className="fas fa-star"
                        color="primary" />}
                </div>
            ),
            selectedOptionFormat: (<Person
                username={recipientName}
                {...(hasText ? {
                    email: formattedPhone,
                    subtitleLabel: 'Phone'
                } : {
                    isUsernameOnly: true,
                    avatarSize: '2em'
                })} />)
        });
    });
    const textingControls = (
        <>
            <TextingControls 
                onChangeSender={(_, { value }) => {onChangeNumbers({ customer_phone: value, contact_phone: to, recipientId, isAccount })}} 
                onChangeRecipient={(_, { value }) => {onChangeNumbers({ contact_phone: value, customer_phone: from, recipientId, isAccount })}}
                from={from} 
                to={to} 
                hasTexting={hasText && recipientPhones.length} 
                recipientPhones={recipientPhones} 
                senderPhones={textPhones} 
                users={[
                    { key: 'all', value: 'all', text: 'All' },
                    ...userOptions.map(user => ({
                        key: user.id,
                        value: user.id,
                        text: `${user.first_name} ${user.last_name}`,
                        content: (
                            <div className='ChatBox__dropdownOption--user'>
                                <ClippedContent priority>
                                    {user.first_name + " " + user.last_name}
                                </ClippedContent>
                            </div>
                        ),
                    }))
                ]} 
                userFieldName='user' 
                isUserDropdownDisabled={messages.length <= 0} 
                otherControls={isUnknownContact && (
                    <Popup 
                        className='ChatBox__popup'
                        on={'click'}
                        onOpen={() => {
                            addContactModal?.openModal({
                                phone: PhoneUtil.sanitizePhoneNumber(to)
                            })
                        }}
                        open={addContactModal?.isVisible}
                        onClose={addContactModal?.closeModal}
                        trigger={(
                            <ButtonLink 
                                onClick={() => {
                                    addContactModal?.openModal({
                                        phone: PhoneUtil.sanitizePhoneNumber(to)
                                    })
                                }} 
                                noPadding
                            >
                                <Icon className='fas fa-plus-circle' /> New/Existing Contact
                            </ButtonLink>
                        )}
                        content={(
                            <div className='ui modal visible active Modal medium ChatBox__addContactModal'>
                                <CloseableContent onClose={addContactModal?.closeModal}>
                                    <AddContactModal
                                        isOfficeView
                                        unknownNumber={addContactModal?.data?.phone}
                                        onClose={addContactModal?.closeModal}
                                        onSuccessCallback={() => {
                                            onReadMessages(to, from)
                                        }}
                                    />
                                </CloseableContent>
                            </div>
                        )}
                        position='left center'
                        basic
                        offset={[0, 0]}
                        closeOnDocumentClick={false}
                    />
                )}
                {...(isOfficeView ? { mode: 'office-view' } : {})} 
            />
        </>
    );
    const chatThread = hasText ? <ChatThread messages={messages} isReadingMessages={isReadingMessages} /> : null;
    const messageEditor = hasText ? <MessageEditor disabled={recipientOptions.length <= 0} from={from} to={to} isAccount={isAccount} onReplaceRecipientVariable={onReplaceRecipientVariable} onReadMessages={onReadMessages} recipientId={recipientId} isLoadingRecipientDetails={isLoadingRecipientDetails} /> : null;
    if (isOfficeView) {
        return (
            <Segment as={Grid} basic className='OfficeViewTexting'>
                <Grid.Row className='OfficeViewTexting__controls'>
                    <Grid.Column>
                        {textingControls}
                    </Grid.Column>
                </Grid.Row>
                <Segment.Group divided as={Grid.Row} stretched horizontal className='OfficeViewTexting__message-box basic divided' columns='equal'>
                    <Segment as={Grid.Column} basic className='OfficeViewTexting__messages'>
                        {chatThread}
                    </Segment>
                    <Segment as={Grid.Column} basic className='OfficeViewTexting__text-form'>
                        {messageEditor}
                    </Segment>
                </Segment.Group>
            </Segment>
        )
    }
    return (
        <Segment.Group className='basic'>
            {textingControls}
            {hasText ? (
                <>
                    {chatThread}
                    <Segment>
                        {messageEditor}
                    </Segment>
                </>
            ) : selectedTextActivityLog ? <div style={{padding: '10px'}}>{selectedTextActivityLog}</div> : (
                <EmptyListPlaceholder
                    title="Texting feature requires purchase."
                    subtitle={
                        <span>
                            You can <Link to={`/contact/${recipientId}/timeline`}><Icon className='fas fa-history' />log previous texts</Link> or <Link to={`/contact/${recipientId}/tasks`}><Icon className='far fa-clipboard-check' />create a text task</Link>
                        </span>
                    } wrapped={false}
                />
            )}
        </Segment.Group>
    )
}

export default ChatBox;
